'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetHistoryCategory, GetHistoryEntry, GetHistoryFilters } from '@repo/schema/history';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@repo/ui/components';
import { History } from '@repo/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { EmptyState } from './history-content/history-empty-state.tsx';
import { HistoryDaySection } from './history-content/history-day-section.tsx';
import { HistoryFilters } from './history-content/history-filters.tsx';
import { groupHistoryEntries, type HistoryDay, type HistoryEntity, HistoryEntityContext } from './history-utils/index.ts';
export type HistoryContentProps = {
  category?: GetHistoryCategory;
  // The search the entries were fetched with; defaults to `search` when it isn't debounced.
  committedSearch?: string;
  entity: HistoryEntity;
  entries: readonly GetHistoryEntry[];
  filters: GetHistoryFilters;
  historyIdentity?: string;
  isFetchingNextPage?: boolean;
  isFetchingResults?: boolean;
  isPlaceholderData?: boolean;
  onCategoryChange: (_category: GetHistoryCategory | undefined) => void;
  onSearchChange: (_search: string) => void;
  onUserChange: (_userId: number | undefined) => void;
  search: string;
  userId?: number;
};
const getInitialOpenDays = (days: readonly HistoryDay[]) => Object.fromEntries(days.slice(0, 2).map(day => [day.id, true]));
export const HistoryContent = t0 => {
  const $ = _c(46);
  const {
    category,
    committedSearch,
    entity,
    entries,
    filters,
    historyIdentity: t1,
    isFetchingNextPage: t2,
    isFetchingResults: t3,
    isPlaceholderData: t4,
    onCategoryChange,
    onSearchChange,
    onUserChange,
    search,
    userId
  } = t0;
  const historyIdentity = t1 === undefined ? "" : t1;
  const isFetchingNextPage = t2 === undefined ? false : t2;
  const isFetchingResults = t3 === undefined ? false : t3;
  const isPlaceholderData = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== entries) {
    t5 = groupHistoryEntries(entries);
    $[0] = entries;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const days = t5;
  let t6;
  if ($[2] !== days) {
    t6 = () => getInitialOpenDays(days);
    $[2] = days;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const [openDays, setOpenDays] = useState(t6);
  const previousHistoryIdentity = useRef(historyIdentity);
  let t7;
  if ($[4] !== category || $[5] !== committedSearch || $[6] !== search || $[7] !== userId) {
    t7 = category !== undefined || userId !== undefined || (committedSearch ?? search).trim() !== "";
    $[4] = category;
    $[5] = committedSearch;
    $[6] = search;
    $[7] = userId;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const isFiltered = t7;
  let t8;
  let t9;
  if ($[9] !== days || $[10] !== historyIdentity || $[11] !== isPlaceholderData) {
    t8 = () => {
      if (!isPlaceholderData && previousHistoryIdentity.current !== historyIdentity) {
        previousHistoryIdentity.current = historyIdentity;
        setOpenDays(getInitialOpenDays(days));
      }
    };
    t9 = [days, historyIdentity, isPlaceholderData];
    $[9] = days;
    $[10] = historyIdentity;
    $[11] = isPlaceholderData;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CardTitle className="flex items-center gap-2 text-2xl leading-8 font-extrabold" data-sentry-element="CardTitle" data-sentry-source-file="history-content.tsx"><History aria-hidden="true" className="size-5 shrink-0" data-sentry-element="History" data-sentry-source-file="history-content.tsx" />History</CardTitle>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== entity.name) {
    t11 = entity.name.toLowerCase();
    $[15] = entity.name;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <div className="min-w-0">{t10}<CardDescription className="text-base leading-6" data-sentry-element="CardDescription" data-sentry-source-file="history-content.tsx">All changes made to this {t11}</CardDescription></div>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== category || $[20] !== filters || $[21] !== onCategoryChange || $[22] !== onSearchChange || $[23] !== onUserChange || $[24] !== search || $[25] !== userId) {
    t13 = <HistoryFilters category={category} filters={filters} onCategoryChange={onCategoryChange} onSearchChange={onSearchChange} onUserChange={onUserChange} search={search} userId={userId} data-sentry-element="HistoryFilters" data-sentry-source-file="history-content.tsx" />;
    $[19] = category;
    $[20] = filters;
    $[21] = onCategoryChange;
    $[22] = onSearchChange;
    $[23] = onUserChange;
    $[24] = search;
    $[25] = userId;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="history-content.tsx"><div className="flex min-w-0 flex-col gap-4 @3xl/card-header:flex-row @3xl/card-header:items-center @3xl/card-header:justify-between">{t12}{t13}</div></CardHeader>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== days || $[31] !== isFiltered || $[32] !== openDays) {
    t15 = days.length === 0 ? <EmptyState filtered={isFiltered} /> : <div>{days.map(day => <HistoryDaySection day={day} key={day.id} onToggle={() => setOpenDays(current => ({
        ...current,
        [day.id]: !current[day.id]
      }))} open={openDays[day.id] ?? false} />)}</div>;
    $[30] = days;
    $[31] = isFiltered;
    $[32] = openDays;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== isFetchingNextPage || $[35] !== isFetchingResults) {
    t16 = isFetchingNextPage || isFetchingResults ? <div aria-live="polite" className="flex items-center justify-center gap-2 border-t px-4 py-3 text-sm text-muted-foreground"><Spinner /> {isFetchingNextPage ? "Loading earlier changes\u2026" : "Updating history\u2026"}</div> : null;
    $[34] = isFetchingNextPage;
    $[35] = isFetchingResults;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <CardContent className="@container/history space-y-3 px-0" data-sentry-element="CardContent" data-sentry-source-file="history-content.tsx">{t15}{t16}</CardContent>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t17) {
    t18 = <Card className="w-full min-w-0" data-sentry-element="Card" data-sentry-source-file="history-content.tsx">{t14}{t17}</Card>;
    $[40] = t14;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== entity || $[44] !== t18) {
    t19 = <HistoryEntityContext value={entity} data-sentry-element="HistoryEntityContext" data-sentry-component="HistoryContent" data-sentry-source-file="history-content.tsx">{t18}</HistoryEntityContext>;
    $[43] = entity;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
};